'use client'

import * as React from 'react'
import { cn } from '@cocoonspace/ui/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { Avatar as AvatarPrimitive } from 'radix-ui'

const avatarVariants = cva(
	'relative flex shrink-0 overflow-hidden rounded-full',
	{
		defaultVariants: {
			role: 'admin',
			size: 'sm',
		},
		variants: {
			role: {
				account_admin: 'bg-primary',
				account_guest: '',
				admin: 'bg-primary',
				guest: '',
				super_admin: 'bg-primary',
				user: 'bg-primary/50',
			},
			size: {
				sm: 'size-[32px] text-[14px]',
				lg: 'size-[50px] text-[18px]',
				xl: 'size-[80px] text-[24px]',
			},
			status: {
				enabled: '',
				suspended: 'bg-slate-500',
			},
		},
	},
)

const Avatar = React.forwardRef<
	React.ElementRef<typeof AvatarPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> &
		VariantProps<typeof avatarVariants>
>(({ role, status, size, className, ...props }, ref) => (
	<AvatarPrimitive.Root
		className={cn(avatarVariants({ className, role, size, status }))}
		ref={ref}
		{...props}
	/>
))

Avatar.displayName = AvatarPrimitive.Root.displayName

const AvatarImage = React.forwardRef<
	React.ElementRef<typeof AvatarPrimitive.Image>,
	React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
	<AvatarPrimitive.Image
		className={cn('aspect-square size-full', className)}
		ref={ref}
		{...props}
	/>
))

AvatarImage.displayName = AvatarPrimitive.Image.displayName

const AvatarFallback = React.forwardRef<
	React.ElementRef<typeof AvatarPrimitive.Fallback>,
	React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
	<AvatarPrimitive.Fallback
		className={cn(
			'text-primary-foreground flex size-full items-center justify-center rounded-full font-bold uppercase',
			className,
		)}
		ref={ref}
		{...props}
	/>
))

AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName

export { Avatar, AvatarFallback, AvatarImage }
